import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { Loader2, Linkedin, UserPlus, MessageSquare } from 'lucide-react';
import api from '../../lib/api';
import { toast } from 'sonner';

// Add a LinkedIn task by hand (no AI). Reusable from Outbox and Lead Activity.
export const ManualTaskDialog = ({ open, onOpenChange, lead, onCreated }) => {
  const [action, setAction] = useState('connect');
  const [message, setMessage] = useState('');
  const [linkedinUrl, setLinkedinUrl] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setAction('connect');
      setMessage('');
      setLinkedinUrl(lead?.linkedin_url || '');
    }
  }, [open, lead]);

  const maxLen = action === 'connect' ? 300 : 8000;

  const submit = async () => {
    if (!message.trim()) { toast.error('Write a message first'); return; }
    if (!lead?.id) { toast.error('No lead selected'); return; }
    setSaving(true);
    try {
      const res = await api.post(`/api/linkedin-tasks/${lead.id}/manual`, {
        action, message: message.trim(), linkedin_url: linkedinUrl.trim() || undefined,
      });
      toast.success('LinkedIn task added to the Outbox');
      onCreated?.(res.data?.task);
      onOpenChange(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not create task');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="manual-linkedin-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Linkedin className="h-5 w-5 text-sky-500" /> New LinkedIn task
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="flex gap-2">
            <button
              type="button" onClick={() => setAction('connect')} data-testid="manual-action-connect"
              className={`flex-1 flex items-center justify-center gap-1.5 rounded-lg border py-2 text-sm font-medium transition-colors ${action === 'connect' ? 'bg-sky-500/10 border-sky-500 text-sky-600' : 'border-border text-muted-foreground hover:text-foreground'}`}
            >
              <UserPlus className="h-3.5 w-3.5" /> Connection request
            </button>
            <button
              type="button" onClick={() => setAction('message')} data-testid="manual-action-message"
              className={`flex-1 flex items-center justify-center gap-1.5 rounded-lg border py-2 text-sm font-medium transition-colors ${action === 'message' ? 'bg-sky-500/10 border-sky-500 text-sky-600' : 'border-border text-muted-foreground hover:text-foreground'}`}
            >
              <MessageSquare className="h-3.5 w-3.5" /> Direct message
            </button>
          </div>

          {!lead?.linkedin_url && (
            <div>
              <Label className="text-xs">LinkedIn profile URL</Label>
              <Input
                value={linkedinUrl} onChange={(e) => setLinkedinUrl(e.target.value)}
                placeholder="https://www.linkedin.com/in/..." className="mt-1"
                data-testid="manual-linkedin-url"
              />
            </div>
          )}

          <div>
            <div className="flex items-center justify-between">
              <Label className="text-xs">{lead?.name ? `Message to ${lead.name.split(' ')[0]}` : 'Message'}</Label>
              <span className={`text-[11px] ${message.length > maxLen ? 'text-destructive' : 'text-muted-foreground'}`}>{message.length}/{maxLen}</span>
            </div>
            <Textarea
              value={message} onChange={(e) => setMessage(e.target.value)}
              rows={5} maxLength={maxLen} className="mt-1 resize-none"
              placeholder={action === 'connect' ? 'Hi {first name}, I noticed…' : 'Write your message…'}
              data-testid="manual-message-textarea"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={saving || !message.trim()} data-testid="manual-task-submit">
            {saving ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : null} Add to Outbox
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
